export interface GrowthWheel {
  id: number
  name: string
  version?: number
  status?: GrowthWheelVersionStatus
}

export type GrowthWheelAssessmentSessionStatus = 'in_progress' | 'completed'

export interface GrowthWheelAssessmentSession {
  id: number
  name: string
  status: GrowthWheelAssessmentSessionStatus
  userId: number
  growthWheelId: number
  growthWheelName?: string
  growthWheelVersion?: number
  growthWheelStatus?: GrowthWheelVersionStatus
  createdAt: string
  completedAt?: string | null
}

export interface SubCompetency {
  id: number
  name: string
  description: string
}

export interface CompetencyEvaluation {
  id: number
  selfLevel: number | null
  selfNote: string | null
  leaderLevel: number | null
  leaderNote: string | null
  focus: boolean
}

export interface SubCompetencyWithEvaluation extends SubCompetency {
  competencyName?: string
  competencyId?: number
  levelDescriptions: { [key: number]: string }
  evaluation: CompetencyEvaluation | null
}

export interface Competency {
  id: number
  name: string
  description: string
  subCompetencies?: SubCompetencyWithEvaluation[]
}

export interface GetGrowthWheelByGuildIdDto {
  guildId: number
}

export interface GetGrowthWheelByUserIdDto {
  userId: number
  assessmentSessionId?: number | null
}

export interface UpdateCompetencyEvaluationPayload {
  selfLevel?: number | null
  selfNote?: string | null
  leaderLevel?: number | null
  leaderNote?: string | null
  leaderId?: number | null
  focus?: boolean | null
}

export interface UpdateCompetencyEvaluationDto {
  /** Use 0 when no evaluation row exists yet (lazy create on save). */
  evaluationId: number
  userId: number
  subCompetencyId: number
  assessmentSessionId?: number
  payload: UpdateCompetencyEvaluationPayload
}

export interface GrowthWheelAssessmentResponse {
  competencies: Competency[]
  isCompleted: boolean
  completedAt: string | null
  assessmentSession: GrowthWheelAssessmentSession | null
  growthWheel: GrowthWheel | null
}

export interface GrowthWheelAssessmentComparisonCompetency {
  name: string
  scores: Record<number, number>
}

export interface GrowthWheelAssessmentComparisonResponse {
  sessions: GrowthWheelAssessmentSession[]
  competencies: GrowthWheelAssessmentComparisonCompetency[]
}

export interface GetGrowthWheelAssessmentComparisonDto {
  userId: number
  assessmentSessionIds: number[]
  scoreField: 'selfLevel' | 'leaderLevel'
}

export interface CompleteGrowthWheelAssessmentDto {
  growthWheelId: number
  userId: number
}

export interface CompleteGrowthWheelAssessmentResponse {
  isCompleted: boolean
  completedAt: string
  session?: GrowthWheelAssessmentSession
}

export interface StartGrowthWheelAssessmentDto {
  growthWheelId: number
  userId: number
  name: string
}

export interface StartGrowthWheelAssessmentResponse {
  isCompleted: false
  completedAt: null
  session: GrowthWheelAssessmentSession
}

export interface GrowthWheelSummary {
  id: number
  name: string
  guildId: number
  guildName: string
  competenciesCount: number
  subCompetenciesCount: number
  contributorsCount: number
  hasContent: boolean
}

export interface CreateGrowthWheelForGuildDto {
  guildId: number
}

export interface GrowthWheelEditorStructure {
  growthWheel: {
    id: number
    name: string
    guildId: number
    guildName: string
    version: number
    status: GrowthWheelVersionStatus
  }
  competencies: Array<{
    id: number
    name: string
    description: string
    sortOrder: number
    subCompetencies: Array<{
      id: number
      name: string
      description: string
      sortOrder: number
      competencyId: number
      levelDescriptions: Record<number, string>
    }>
  }>
}

export type GrowthWheelPath = '/growth-wheel' | '/growth-wheel-admin'

export type GrowthWheelVersionStatus = 'draft' | 'active' | 'archived'

export type WheelVersionDisplayStatus = 'draft' | 'active' | 'historical'

export interface GrowthWheelVersionMeta {
  id: number
  version: number
  label: string
  status: GrowthWheelVersionStatus
  createdAt: string
  guildId: number
  guildName: string
  canDelete: boolean
  hasEvaluations: boolean
}

export const toWheelVersionDisplayStatus = (
  status: GrowthWheelVersionStatus,
): WheelVersionDisplayStatus =>
  status === 'archived' ? 'historical' : status
